/**
 * Row-alignment and part-hiding for the SHARED storefront tiles
 * (`HecProductTileCms` → standard / `LmnProductTile` / `TelehealthProductTile`)
 * when the Builder Product Tile or Featured Product Slider renders them —
 * applied from OUTSIDE, as classes on our wrapper, so those shared files stay
 * untouched and PLP / search render exactly as before.
 *
 * ⚠ Coupling: every selector below keys on a shared tile's `data-testid` (the
 * QA contract, `docs/test-ids.md`) and on its DOM shape. If a tile renames one
 * of these ids or restructures its body, the matching override silently stops
 * applying. Two guards turn that drift into a failing test:
 * `dynamic-product-tile.test.tsx` renders the REAL tiles and checks every
 * selector on the wrapper matches an element, and
 * `shared-tile-overrides.test.ts` checks the tiles' source still carries the
 * ids and DOM shape the selectors key on.
 *
 * Class strings are literal (not built) so Tailwind's scanner emits them.
 */
import { cn } from "~/lib/utils";

import type { SharedTileOverridesInput } from "./types";

/**
 * Title wrapper reserves 2 lines PLUS the 8px gap (56px, not 48px). The
 * shared tiles put the link's `mb-[.5rem]` inside a `min-h-[3rem]` wrapper and
 * `line-clamp` stops it collapsing, so a 2-line title is 8px taller than a
 * 1-line one (measured). Targets the wrapper as "the div whose direct child is
 * the name link" — all three tiles share `product-tile-name-${sku}`.
 */
const UNIFORM_TITLE =
  "[&_div:has(>[data-testid^='product-tile-name-'])]:min-h-[3.5rem]";

/** Author switched badges off — standard tile only (see `sharedTileOverrides`). */
const HIDE_BADGES = "[&_[data-testid^='product-tile-badge-']]:hidden";

/** Author switched price off — standard tile only. */
const HIDE_PRICE = "[&_[data-testid^='product-tile-price-']]:hidden";

/**
 * An unpriced telehealth tile (no Builder-authored row) omits its price line,
 * so its CTA rides one line higher than its priced neighbours. Push the CTA
 * down by exactly that line — `1lh` is the CTA's own line height, which equals
 * the price's (same inherited type, measured 20.8px) — plus the price's 8px
 * `mb`. Applies only when the CTA has no price sibling.
 */
const TELEHEALTH_PRICE_SPACER =
  "[&_div:not(:has(>[data-testid^='product-tile-price-']))>[data-testid^='telehealth-tile-get-started-']]:mt-[calc(1lh_+_0.5rem)]";

/**
 * Classes for the wrapper around a shared tile. Badge / price toggles apply to
 * the STANDARD tile only — same as before this moved outside: the LMN tile has
 * no price, and the telehealth tile's Rx badge and Builder price are part of
 * its regulated treatment (ET-225..232), not author toggles.
 */
export function sharedTileOverrides({
  kind,
  showBadges,
  showPrice,
}: SharedTileOverridesInput): string {
  return cn(
    UNIFORM_TITLE,
    kind === "standard" && !showBadges && HIDE_BADGES,
    kind === "standard" && !showPrice && HIDE_PRICE,
    kind === "telehealth" && TELEHEALTH_PRICE_SPACER,
  );
}
